import React from "react";
import { ChevronLeft, Check, Lock, Star, ArrowRight, ArrowLeft } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { PAY_METHODS } from "./transportData";

export const BookingHeader = ({ title, onBack, right }) => (
  <header className="tp-header">
    <button className="tp-back" data-testid="tp-back-btn" aria-label="Retour" onClick={onBack}>
      <ChevronLeft size={18} />
    </button>
    <h1>{title}</h1>
    <div className="tp-hright">{right}</div>
  </header>
);

export const Pills = ({ options, value, onChange, testid }) => (
  <div className="tp-pills" data-testid={testid}>
    {options.map((o) => (
      <button key={o} className={`tp-pill ${value === o ? "on" : ""}`} data-testid={`${testid}-${o}`} onClick={() => onChange(o)}>{o}</button>
    ))}
  </div>
);

export const PrimaryBtn = ({ children, onClick, testid, disabled, ghost }) => (
  <button className={`tp-btn ${ghost ? "ghost" : ""}`} data-testid={testid} onClick={onClick} disabled={disabled}>{children}</button>
);

export const Stepper = ({ value, onChange, min = 1, max = 4, unit }) => (
  <div className="tp-stepper">
    <button data-testid="tp-stepper-minus" onClick={() => onChange(Math.max(min, value - 1))}>−</button>
    <span data-testid="tp-stepper-value">{value} {unit}{value > 1 ? "s" : ""}</span>
    <button data-testid="tp-stepper-plus" onClick={() => onChange(Math.min(max, value + 1))}>+</button>
  </div>
);

export const OptRow = ({ title, sub, price, checked, onToggle, testid, chevron }) => (
  <div className="tp-optrow" data-testid={testid} onClick={onToggle}>
    <span className={`tp-check ${checked ? "on" : ""}`}>{checked && <Check size={13} color="#fff" strokeWidth={3} />}</span>
    <span className="t"><h4>{title}</h4>{sub && <p>{sub}</p>}</span>
    <span className="px">{price}</span>
    {chevron}
  </div>
);

export const PaymentMethods = ({ value, onChange, methods = PAY_METHODS }) => (
  <div data-testid="tp-pay-methods">
    {methods.map((m) => (
      <div key={m.id} className={`tp-payrow ${value === m.id ? "on" : ""}`} data-testid={`tp-pay-${m.id}`} onClick={() => onChange(m.id)}>
        <span className="tp-paylogo" style={{ background: m.bg, color: m.dark ? "#1F2937" : "#fff" }}>{m.text}</span>
        <h4>{m.name}</h4>
        <span className={`tp-radio ${value === m.id ? "on" : ""}`} />
      </div>
    ))}
  </div>
);

export const PriceRow = ({ label, value, total }) => (
  <div className={`tp-pricerow ${total ? "total" : ""}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);

export const SeatLegend = () => (
  <div className="tp-legend">
    <span><i style={{ background: "#DFF5E9", border: "1px solid #BBE7CF" }} />Disponible</span>
    <span><i style={{ background: "#0E7A4F" }} />Sélectionné</span>
    <span><i style={{ background: "#E8ECE9", border: "1px solid #D5DCD6" }} />Indisponible</span>
  </div>
);

export const PlaneSeatMap = ({ taken, selected, onSelect }) => {
  const cols = ["A", "B", "C", "D", "E", "F"];
  return (
    <div className="tp-seatgrid" data-testid="tp-seatmap">
      <div className="tp-seatrow" style={{ marginBottom: 2 }}>
        {cols.slice(0, 3).map((c) => <span key={c} className="tp-aisle" style={{ width: 30 }}>{c}</span>)}
        <span className="tp-aisle" />
        {cols.slice(3).map((c) => <span key={c} className="tp-aisle" style={{ width: 30 }}>{c}</span>)}
      </div>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((row) => (
        <div className="tp-seatrow" key={row}>
          {cols.map((c, idx) => {
            const id = `${row}${c}`;
            const isTaken = taken.has(id);
            return (
              <React.Fragment key={id}>
                {idx === 3 && <span className="tp-aisle">{row}</span>}
                <button className={`tp-seat ${isTaken ? "taken" : ""} ${selected === id ? "sel" : ""}`}
                  data-testid={`tp-seat-${id}`} disabled={isTaken} onClick={() => onSelect(id)}>
                  {isTaken ? "×" : id}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export const BusSeatMap = ({ taken, selected, onSelect }) => (
  <div className="tp-bus-wrap" data-testid="tp-seatmap">
    <span className="tp-bus-fen l"><ArrowRight size={12} /> Fenêtre</span>
    <span className="tp-bus-fen r">Fenêtre <ArrowLeft size={12} /></span>
    <div className="tp-bus-frame">
      <div className="tp-bus-front">Avant</div>
      <div className="tp-bus-rows">
        <span className="tp-bus-strip" />
        <div className="tp-bus-seats">
          {Array.from({ length: 7 }, (_, i) => i).map((r) => (
            <div className="tp-bus-row" key={r}>
              {[r * 2 + 1, r * 2 + 2].map((n, idx) => {
                const id = String(n).padStart(2, "0");
                const isTaken = taken.has(id);
                return (
                  <React.Fragment key={id}>
                    {idx === 1 && <span className="tp-bus-aisle" />}
                    <button className={`tp-seatb ${isTaken ? "taken" : ""} ${selected === id ? "sel" : ""}`}
                      data-testid={`tp-seat-${id}`} disabled={isTaken} onClick={() => onSelect(id)}>
                      {id}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          ))}
        </div>
        <span className="tp-bus-strip" />
      </div>
    </div>
  </div>
);

export const RatingLine = ({ rating, reviews }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, color: "#4A5D50", fontWeight: 700 }}>
    <Star size={10} color="#F5A623" fill="#F5A623" /> {rating} <span style={{ color: "#A6B2A9" }}>({reviews} avis)</span>
  </span>
);

export const SecureNote = () => (
  <div className="tp-secure"><Lock size={11} /> Paiement sécurisé par nos partenaires</div>
);

export const TicketCard = ({ brand, brandColor, badge, reference, passenger, from, to, depTime, arrTime, cells }) => (
  <div className="tp-ticket" data-testid="tp-ticket">
    <div className="head">
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: 800, fontSize: 13 }}>
        <span className="tp-logo" style={{ background: brandColor || "#0E7A4F" }}>{brand.slice(0, 3).toUpperCase()}</span>
        {brand}
      </span>
      <span className="tp-tag" style={{ background: "rgba(16,185,129,0.2)", borderColor: "rgba(16,185,129,0.5)", color: "#A7F3D0" }}>{badge}</span>
    </div>
    <div className="qrbox">
      <QRCodeSVG value={reference} size={124} level="M" data-testid="tp-ticket-qr" />
      <div style={{ fontSize: 11.5, fontWeight: 800, marginTop: 8 }}>{reference}</div>
      <div style={{ fontSize: 10.5, color: "#7B8B7F", fontWeight: 700 }}>{passenger}</div>
    </div>
    <div className="route">
      <span className="c"><span className="code">{from.code}</span><br /><span className="city">{from.city}</span><br /><span className="time">{depTime}</span></span>
      <span style={{ flex: 1, borderBottom: "1.5px dashed #C9D6CC", margin: "0 12px" }} />
      <span className="c"><span className="code">{to.code}</span><br /><span className="city">{to.city}</span><br /><span className="time">{arrTime}</span></span>
    </div>
    <div className="grid">
      {cells.map((c) => (
        <span key={c.lab}><span className="lab">{c.lab}</span><br /><span className="v">{c.v}</span></span>
      ))}
    </div>
  </div>
);
